// Fasting timer state + log. Cloud-first when signed in (mystack_fasting_sessions).
//
// localStorage cache for instant paint + offline.

import { supabase, isCloudMode, currentUserSync } from './supabase';

export type FastingActive = {
  id: string;         // uuid
  started_at: string; // ISO
  target_hours: number;
  protocol: string;   // "16:8", "OMAD", "Custom 18:6"
};

export type FastingEntry = {
  id: string;
  started_at: string;
  ended_at: string;
  target_hours: number;
  protocol: string;
};

const K_ACTIVE = 'mystack:fasting:active:v1';
const K_LOG    = 'mystack:fasting:log:v1';

function safeParse<T>(s: string | null, fallback: T): T {
  if (!s) return fallback;
  try { return JSON.parse(s) as T; } catch { return fallback; }
}

import { uuidV4 } from './analytics';

export function loadActive(): FastingActive | null {
  return safeParse<FastingActive | null>(localStorage.getItem(K_ACTIVE), null);
}

export async function startFast(protocol: string, targetHours: number): Promise<FastingActive> {
  const a: FastingActive = {
    id: uuidV4(),
    started_at: new Date().toISOString(),
    target_hours: targetHours,
    protocol,
  };
  localStorage.setItem(K_ACTIVE, JSON.stringify(a));
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      await supabase.from('mystack_fasting_sessions').insert({
        id: a.id, owner_id: u.id,
        protocol: a.protocol, target_hours: a.target_hours,
        started_at: a.started_at, ended_at: a.started_at, // sentinel until end
        abandoned: false,
      });
    }
  }
  return a;
}

export async function endFast(success: boolean): Promise<void> {
  const a = loadActive();
  if (!a) return;
  const endedAt = new Date().toISOString();
  if (success) {
    const log = safeParse<FastingEntry[]>(localStorage.getItem(K_LOG), []);
    log.push({ id: a.id, started_at: a.started_at, ended_at: endedAt, target_hours: a.target_hours, protocol: a.protocol });
    localStorage.setItem(K_LOG, JSON.stringify(log));
  }
  localStorage.removeItem(K_ACTIVE);
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      if (success) {
        await supabase.from('mystack_fasting_sessions').upsert({
          id: a.id, owner_id: u.id,
          protocol: a.protocol, target_hours: a.target_hours,
          started_at: a.started_at, ended_at: endedAt,
          abandoned: false,
        });
      } else {
        // Abandon: delete row (matches APK behavior).
        await supabase.from('mystack_fasting_sessions')
          .delete().eq('owner_id', u.id).eq('id', a.id);
      }
    }
  }
}

export function loadLog(): FastingEntry[] {
  return safeParse<FastingEntry[]>(localStorage.getItem(K_LOG), []);
}

export async function loadLogAsync(): Promise<FastingEntry[]> {
  if (await isCloudMode()) {
    const u = currentUserSync();
    if (u) {
      const { data, error } = await supabase
        .from('mystack_fasting_sessions')
        .select('id, protocol, target_hours, started_at, ended_at, abandoned')
        .eq('owner_id', u.id)
        .eq('abandoned', false)
        .order('started_at', { ascending: false });
      if (!error && data) {
        const list = data.map((r) => ({
          id: (r as any).id,
          started_at: (r as any).started_at,
          ended_at: (r as any).ended_at,
          target_hours: (r as any).target_hours,
          protocol: (r as any).protocol,
        }));
        localStorage.setItem(K_LOG, JSON.stringify(list));
        return list;
      }
    }
  }
  return loadLog();
}
